import React from "react";
import { Helmet } from "react-helmet-async";
import { STORE, IMAGES } from "../lib/store";
import { useEdit } from "../lib/content";

const SITE_NAME = "Regal Jewels";
const DEFAULT_OG = IMAGES.hero;

export default function Seo({
  title,
  description,
  path = "",
  image,
  type = "website",
  noindex = false,
  jsonLd = null,
  keywords = null,
}) {
  const { get } = useEdit();
  const ovTitle = get(`seo.${path}.title`, "");
  const ovDesc = get(`seo.${path}.description`, "");
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = `${origin}${path}`;
  const effectiveTitle = ovTitle || title;
  const fullTitle = effectiveTitle ? `${effectiveTitle} | ${SITE_NAME}` : `${SITE_NAME} | 22K Gold & Diamond Jewelry Store in Chicago`;
  const desc =
    ovDesc || description ||
    "Regal Jewels — jewelry store in Chicago. 18k, 22k & 24k gold, certified diamonds, silver & fine jewelry on Devon Avenue. USA-wide delivery.";
  const ogImage = image || DEFAULT_OG;
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && keywords.length > 0 && <meta name="keywords" content={keywords.join(", ")} />}
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index, follow" />}
      <link rel="canonical" href={url} />
      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
      {schemas.map((s, i) => (
        <script key={`${s["@type"] || "schema"}-${i}`} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}

// ---- Reusable JSON-LD builders (real business data only) ----
export function jewelryStoreSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "JewelryStore",
    name: "Regal Jewels",
    image: STORE.logo,
    url: typeof window !== "undefined" ? window.location.origin : "",
    telephone: "+1-773-262-4377",
    email: STORE.email,
    priceRange: "$$-$$$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: "2625 W. Devon Ave.",
      addressLocality: "Chicago",
      addressRegion: "IL",
      postalCode: "60659",
      addressCountry: "US",
    },
    geo: { "@type": "GeoCoordinates", latitude: 41.9976, longitude: -87.6947 },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        opens: "11:00",
        closes: "20:00",
      },
    ],
    sameAs: ["https://regaljewels.com"],
  };
}

export function websiteSchema() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Regal Jewels",
    url: origin,
  };
}

export function breadcrumbSchema(items) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${origin}${it.path}`,
    })),
  };
}

export function productSchema(product, priceOverride) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const price = priceOverride != null ? priceOverride : product.price;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title,
    image: product.images,
    description: product.seoDescription || product.description || product.title,
    sku: product.handle,
    brand: { "@type": "Brand", name: "Regal Jewels" },
    offers: {
      "@type": "Offer",
      url: `${origin}/product/${product.handle}`,
      priceCurrency: "USD",
      price: price,
      availability: product.available
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      seller: { "@type": "Organization", name: "Regal Jewels" },
    },
  };
}

export function webPageSchema({ name, path, description, dateModified }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const s = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name,
    url: `${origin}${path}`,
    description,
    isPartOf: { "@type": "WebSite", name: "Regal Jewels", url: origin },
    publisher: { "@type": "JewelryStore", name: "Regal Jewels", url: origin },
  };
  if (dateModified) s.dateModified = dateModified;
  return s;
}

export function itemListSchema(products, path) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: products.slice(0, 24).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${origin}/product/${p.handle}`,
      name: p.title,
    })),
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function articleSchema(guide) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.title,
    description: guide.summary,
    author: { "@type": "Organization", name: "Regal Jewels" },
    publisher: { "@type": "Organization", name: "Regal Jewels", logo: { "@type": "ImageObject", url: STORE.logo } },
    mainEntityOfPage: `${origin}/education/${guide.slug}`,
  };
}
